import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnInit } from '@angular/core';

@Component({
  selector: 'app-upload-foto',
  template: `
   <div class="upload-container" (dragover)="onDragOver($event)" (drop)="onDrop($event)">
  <!-- Input de arquivo oculto -->
  <input type="file" accept="image/*" (change)="onFileSelected($event)" hidden #fileInput />

  <!-- Se não houver imagem, mostra botão com ícone de upload -->
  @if (!image) {
    <div class="no-image" (click)="openFileSelector()">
      <span class="material-icons upload-icon">cloud_upload</span>
      <span class="upload-text">Clique ou arraste para enviar</span>
      <span class="upload-label">{{ name }}</span>
    </div>
  }

  <!-- Se houver imagem, exibe a pré-visualização -->
  @if (image) {
    <div class="image-preview">
      <img [src]="image" alt="Preview da imagem" />
      
      <!-- Botão de remoção apenas se for permitido -->
      @if (allowRemove) {
        <button mat-icon-button class="remove-btn" (click)="removeImage()">
          <span class="material-icons">close</span>
        </button>
      }
      
      <div class="image-overlay">
        <span class="material-icons">check_circle</span>
        <span class="overlay-text">{{ name }}</span>
      </div>
    </div>
  }
</div>

  `,
  styles: [
    `
    .upload-container {
  border: none;
  padding: 0;
  text-align: center;
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 12px;
  background: transparent;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1.5rem;
  transition: all 0.3s ease;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
}

.upload-container:hover .no-image {
  border-color: #27509b;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.upload-icon {
  font-size: 56px;
  color: #64748b;
  transition: all 0.3s ease;
}

.upload-container:hover .upload-icon {
  color: #27509b;
  transform: scale(1.1);
}

.upload-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
}

.upload-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.image-preview {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  background: rgba(239, 68, 68, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.remove-btn:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.remove-btn .material-icons {
  font-size: 20px;
  color: white;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.image-overlay .material-icons {
  font-size: 24px;
  color: #10b981;
}

.overlay-text {
  font-size: 0.875rem;
  font-weight: 600;
}

    `
  ]
})
export class UploadFotoComponent implements OnInit {
  @Input() image: string | null = null;
  @Input() preloadedImage: string | null = null; // Define uma imagem inicial
  @Input() name: string | null = null;
  @Input() allowRemove: boolean = true; // Permite ou não remover a imagem
  @Output() selectedImage = new EventEmitter<string | null>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    if (this.preloadedImage) {
      this.image = this.preloadedImage;
    }

    if (!this.name) {
      this.name = 'Foto Padrão'; // Define um nome padrão caso o usuário não passe um
    }

    if (this.preloadedImage) {
      this.image = this.preloadedImage;
    }
  }

  openFileSelector(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.loadImage(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.loadImage(file);
    }
  }

  loadImage(file: File): void {
    const reader = new FileReader();
  
    reader.onload = async () => {
      const imageDataUrl = reader.result as string;
  
      try {
        const compressed = await this.compressImage(imageDataUrl, 800, 0.7); // Largura máx: 800px, Qualidade: 70%
        this.image = compressed;
        this.selectedImage.emit(this.image);
      } catch (err) {
        console.error('Erro ao comprimir imagem', err);
      }
    };
  
    reader.readAsDataURL(file);
  }

  // nativo do browser em canvas
  compressImage(dataUrl: string, maxWidth: number, quality: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = dataUrl;
  
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
  
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('Erro ao obter contexto do canvas');
  
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality); // formato JPEG com compressão
        resolve(compressedDataUrl);
      };
  
      img.onerror = (err) => reject(err);
    });
  }

  removeImage(): void {
    if (!this.allowRemove) return;
    this.image = null;
    this.selectedImage.emit(null);
  }
}
